/**
 * @author Jersson Stiven Morales Alza
 * @Copyright (c) Infotrack. All rights reserved.
 */
import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import {
    AppBar,
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
    Toolbar,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Autocomplete } from '@material-ui/lab';

import Texto from '@infotrack/presentacion-componentes/texto';
import { TipoFormulario } from '@infotrack/presentacion-transversales/interfacesComunes';
import { esNumerico } from '@infotrack/utilitarios/expresionesRegulares';
import { IFormularioClientesVistaProps } from '../interfaces';
import { estadoActivo } from 'Infotrack@Transversales/constantes/ConstantesEstados';

const FormularioClientesVista: React.FunctionComponent<IFormularioClientesVistaProps & InjectedIntlProps> = ({
    accionCrearEditar,
    alCambiarMunicipio,
    cambiarInfoFomulario,
    cerrarFormulario,
    clientes,
    entidadesCliente,
    estadoFormulario,
    esMovil,
    intl: { formatMessage },
    tipoFormulario,
    validaciones,
    validarCliente,
}) => (
    <Dialog open={estadoFormulario} fullWidth maxWidth={esMovil ? 'lg' : 'sm'}>
        <AppBar position="static">
            <Toolbar>
                <Grid container justifyContent="center">
                    <Texto
                        align="center"
                        color="inherit"
                        id={
                            tipoFormulario === TipoFormulario.Creacion
                                ? 'clientes.agregarClientes'
                                : 'clientes.modificarClientes'
                        }
                    />
                </Grid>
            </Toolbar>
        </AppBar>
        <DialogContent>
            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item md={5} xs={12}>
                                            <TextField
                                                fullWidth
                                                label={<Texto id="clientes.tipoClienteId" display="inline" />}
                                                name="TipoClienteId"
                                                onChange={cambiarInfoFomulario}
                                                required
                                                select
                                                value={clientes.TipoClienteId}
                                                helperText={
                                                    tipoFormulario === TipoFormulario.Edicion &&
                                                    !entidadesCliente.tiposClientes.find(
                                                        (x) =>
                                                            x.TipoClienteId === parseInt(clientes.TipoClienteId) &&
                                                            x.Estado === estadoActivo
                                                    ) ? (
                                                        <Texto id="clientes.TipoClienteInactivo" />
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                                error={
                                                    tipoFormulario === TipoFormulario.Edicion &&
                                                    !!entidadesCliente.tiposClientes.find(
                                                        (x) =>
                                                            (x.TipoClienteId === parseInt(clientes.TipoClienteId) ||
                                                                clientes.TipoClienteId === '0') &&
                                                            x.Estado === 0
                                                    )
                                                        ? true
                                                        : false
                                                }
                                            >
                                                {entidadesCliente.tiposClientes.filter(
                                                    (x) => x.Estado === true || x.Estado === 1
                                                ).length === 0 ? (
                                                    <MenuItem value="">
                                                        <Texto id="label.sinRegistros" />
                                                    </MenuItem>
                                                ) : (
                                                    entidadesCliente.tiposClientes
                                                        .filter((x) => x.Estado === true || x.Estado === 1)
                                                        .map(({ TipoClienteDescripcion, TipoClienteId }) => (
                                                            <MenuItem key={TipoClienteId} value={TipoClienteId}>
                                                                {TipoClienteDescripcion}
                                                            </MenuItem>
                                                        ))
                                                )}
                                            </TextField>
                                        </Grid>
                                        <Grid item md={5} xs={12}>
                                            <TextField
                                                fullWidth
                                                label={<Texto id="clientes.documento" display="inline" />}
                                                required
                                                name="Documento"
                                                onChange={cambiarInfoFomulario}
                                                value={clientes.Documento}
                                            />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                color="primary"
                                                fullWidth
                                                onClick={validarCliente}
                                                variant="contained"
                                            >
                                                <SearchIcon />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Autocomplete
                                getOptionLabel={({ NombreMunicipio }: any) => NombreMunicipio}
                                onChange={(_: any, municipio: any) => alCambiarMunicipio(municipio)}
                                options={entidadesCliente.municipios}
                                renderInput={(params: any) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        label={formatMessage({ id: 'clientes.ciudadId' })}
                                    />
                                )}
                                noOptionsText={<Texto id="label.sinRegistros" />}
                                value={clientes.Ciudad}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                disabled={validaciones.esJuridico}
                                fullWidth
                                label={<Texto id="clientes.descripcionCliente" display="inline" />}
                                name="DescripcionCliente"
                                onChange={cambiarInfoFomulario}
                                required
                                value={clientes.DescripcionCliente}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                disabled={validaciones.esNatural || clientes.Principal}
                                label={<Texto id="clientes.descripcionSucursal" display="inline" />}
                                name="DescripcionSucursal"
                                onChange={cambiarInfoFomulario}
                                value={clientes.DescripcionSucursal}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label={<Texto id="clientes.nombreContacto" display="inline" />}
                                name="NombreContacto"
                                onChange={cambiarInfoFomulario}
                                required
                                value={clientes.NombreContacto}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label={<Texto id="clientes.telefono" display="inline" />}
                                name="Telefono"
                                onChange={cambiarInfoFomulario}
                                required
                                value={clientes.Telefono}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label={<Texto id="clientes.direccion" display="inline" />}
                                name="Direccion"
                                onChange={cambiarInfoFomulario}
                                required
                                value={clientes.Direccion}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label={<Texto id="clientes.correo" display="inline" />}
                                name="Correo"
                                onChange={cambiarInfoFomulario}
                                required
                                value={clientes.Correo}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label={<Texto id="clientes.diasVencimiento" display="inline" />}
                                name="DiasVencimiento"
                                onChange={(e) => esNumerico(e.target.value) && cambiarInfoFomulario(e)}
                                value={clientes.DiasVencimiento}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label={<Texto id="clientes.codigoBarras" display="inline" />}
                                name="CodigoBarras"
                                onChange={cambiarInfoFomulario}
                                value={clientes.CodigoBarras}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item md={6} xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={Boolean(clientes.Estado)}
                                                onChange={cambiarInfoFomulario}
                                            />
                                        }
                                        label={<Texto id="clientes.estado" display="inline" />}
                                        name="Estado"
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={Boolean(clientes.Principal) || validaciones.esPrincipal}
                                                onChange={cambiarInfoFomulario}
                                                /*onChange={(e) =>
                                                    !validaciones.esPrincipal &&
                                                    !validaciones.esNatural &&
                                                    !validaciones.esSucursal &&
                                                    cambiarInfoFomulario(e)
                                                }*/
                                            />
                                        }
                                        label={<Texto id="clientes.principal" display="inline" />}
                                        name="Principal"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </DialogContent>
        <DialogActions>
            <Grid container justifyContent="flex-end" spacing={4}>
                <Grid item>
                    <Button onClick={cerrarFormulario} color="secondary" variant="contained">
                        <Texto id="boton.cancelar" />
                    </Button>
                </Grid>
                <Grid item>
                    <Button onClick={accionCrearEditar} color="primary" variant="contained">
                        <Texto id="boton.guardar" />
                    </Button>
                </Grid>
            </Grid>
        </DialogActions>
    </Dialog>
);

export default injectIntl(FormularioClientesVista);
